import React, { useState, useEffect } from 'react';
import { Col, Row, formatAmountText, getItemWithExpiration } from '../../lib/utils';
import { useAmountInputCaret } from '../../lib/amountInputCaret';
import { colors } from '../../lib/colors';
import { Body2Regular, Subtitle1Bold } from '../../lib/font';
import dayjs from 'dayjs';
import { useUpdateSubscription, useDeleteSubscription, useRefundSubscription } from '@/features/facilitySubscription/hooks';
import { IFacilitySubscription, PaymentMethod } from '@/features/facilitySubscription/types';
import { handleLockerConflict } from '@/features/facilitySubscription/lockerConflict';
import SubscriptionPauseManager from './SubscriptionPauseManager';
import { TransferKindBadge, TransferredRefundNotice, useTransferredRefundInfo } from './SubscriptionTransferHistory';
import { describeSubscriptionUpdateError, getFacilityTransferLocks, isCenterViewerAccount } from '@/features/facilitySubscription/transfer';
import { hasPendingFacilityTransfer } from '@/features/facilitySubscription/transferPending';

const PAYMENT_OPTIONS: { code: PaymentMethod; label: string }[] = [
    { code: 'card', label: '카드' },
    { code: 'cash', label: '현금' },
    { code: 'transfer', label: '이체' },
];

const getMutationErrorMessage = (error: any, fallback: string) => error?.serverMessage || error?.message || fallback;

interface Props {
    isVisible: boolean;
    onClose: () => void;
    centerId: string;
    memberId: string;
    subscription: IFacilitySubscription;
    createAlert: any;
    /** 있으면 「양도」 버튼을 보인다(이 창을 닫고 부모가 양도 창을 연다) */
    onTransfer?: () => void;
}

const SubscriptionEditModal = ({ isVisible, onClose, centerId, memberId, subscription, createAlert, onTransfer }: Props) => {
    // 양도 표식에 따른 잠금(서버 잠금과 같은 범위). 표식이 없으면 기존 동작 그대로다.
    const transferLocks = getFacilityTransferLocks(subscription);
    // 열람 전용 계정은 양도·환불 진입을 숨긴다(공통 클라이언트·서버도 쓰기를 막지만 버튼부터 보이지 않게).
    const [isViewerAccount] = useState(() => isCenterViewerAccount(getItemWithExpiration('authObject')));
    // 결과를 모르는 양도가 남아 있으면 상태와 상관없이 마무리 진입을 보인다.
    const hasPendingTransfer = !!subscription?._id && hasPendingFacilityTransfer(subscription._id);
    const updateMutation = useUpdateSubscription();
    const deleteMutation = useDeleteSubscription();
    const refundMutation = useRefundSubscription();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showRefundConfirm, setShowRefundConfirm] = useState(false);
    const [refundAmount, setRefundAmount] = useState<string>('');
    const [refundReason, setRefundReason] = useState<string>('');
    // 일부 양도한 원본 환불: 환불 확인을 열 때 이력을 새로 읽고, 다 읽기 전에는 환불을 막는다.
    const transferredInfo = useTransferredRefundInfo({
        centerId,
        subscriptionId: subscription?._id || '',
        enabled: showRefundConfirm && transferLocks.refundNeedsAck && !!subscription?._id,
    });
    const refundBlockedByTransferInfo = transferLocks.refundNeedsAck && !transferredInfo.ready;

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    /*
     * 창을 열 때의 날짜. 날짜는 사용자가 실제로 바꿨을 때만 보낸다.
     * 'YYYY-MM-DD' 를 그대로 보내면 서버가 그날 09:00(KST)으로 저장해 하루끝(23:59:59)으로 저장된 종료일이
     * 15시간 줄고, 오래 열린 창에서 메모만 저장해도 그 사이 양도로 줄어든 기간을 옛 날짜로 되돌린다.
     */
    const [initialDates, setInitialDates] = useState({ startDate: '', endDate: '' });
    // 금액 state 두 개 모두 콤마 없는 숫자 문자열만 담는다. 제출은 그대로 Number(...) 를 쓴다.
    const [price, setPrice] = useState('');
    const amountCaret = useAmountInputCaret({ allowDecimal: true });
    const [paymentDate, setPaymentDate] = useState('');
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
    const [memo, setMemo] = useState('');
    const [lockerNumber, setLockerNumber] = useState('');
    const [lockNumber, setLockNumber] = useState('');

    // Pre-populate form when subscription changes
    useEffect(() => {
        if (subscription) {
            const nextStart = dayjs(subscription.startDate).format('YYYY-MM-DD');
            const nextEnd = dayjs(subscription.endDate).format('YYYY-MM-DD');
            setStartDate(nextStart);
            setEndDate(nextEnd);
            setInitialDates({ startDate: nextStart, endDate: nextEnd });
            amountCaret.dropPendingCaret();
            setPrice(subscription.price != null ? String(subscription.price) : '');
            setPaymentDate(subscription.paymentDate ? dayjs(subscription.paymentDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'));
            setPaymentMethod(subscription.paymentMethod || 'card');
            setMemo(subscription.memo || '');
            setLockerNumber(subscription.lockerNumber || '');
            setLockNumber(subscription.lockNumber || '');
        }
    }, [subscription, amountCaret]);

    // 받은 이용권은 칸 없이 넘어올 수 있다(일부 양도·칸 안 넘김) — 메모만 고칠 때 칸 배정을 강요하지 않는다.
    const lockerRequired =
        subscription?.category === 'locker' &&
        subscription?.status === 'active' &&
        !transferLocks.isTarget &&
        !lockerNumber.trim();
    /*
     * 날짜를 바꿨을 때만 검사한다. 바꾼 날짜는 서버가 그날 09:00 으로 저장하므로 같은 날이면 0시간·음수 기간이
     * 된다 → 바꿨다면 종료일이 시작일보다 뒤(다른 날)여야 한다. 안 바꿨으면 원래 시각을 그대로 두므로
     * 1일 이용권(같은 날 0시~하루끝, 예: 1일 양도)의 메모 수정도 막지 않는다.
     */
    const datesChanged = startDate !== initialDates.startDate || endDate !== initialDates.endDate;
    const dateError = datesChanged && startDate && endDate && startDate >= endDate ? '종료일은 시작일보다 뒤여야 합니다.' : null;
    const isValid = startDate && endDate && price !== '' && paymentDate && !dateError && !lockerRequired;

    const openRefund = () => {
        amountCaret.dropPendingCaret();
        // 일부 양도한 원본은 넘긴 기간까지 돌려주지 않도록 빈칸으로 시작한다.
        setRefundAmount(transferLocks.refundNeedsAck ? '' : String(subscription?.price ?? 0));
        setRefundReason('');
        setShowRefundConfirm(true);
    };

    const handleRefund = async () => {
        if (refundBlockedByTransferInfo) return;
        const amount = Number(refundAmount);
        if (!amount || amount <= 0) {
            createAlert('환불 실패', '환불 금액을 입력해주세요.', true);
            return;
        }
        if (amount > (subscription?.price ?? 0)) {
            createAlert('환불 실패', '환불 금액이 결제 금액을 초과할 수 없습니다.', true);
            return;
        }
        try {
            await refundMutation.mutateAsync({
                centerId,
                subscriptionId: subscription._id,
                memberId,
                body: {
                    refundAmount: amount,
                    refundReason: refundReason.trim() || '관리자 환불',
                    ...(transferLocks.refundNeedsAck ? { acknowledgeTransferred: true as const } : {}),
                },
            });
            setShowRefundConfirm(false);
            onClose();
            createAlert('환불 완료', `${amount.toLocaleString()}원 환불이 처리되었습니다.`, false);
        } catch (error) {
            createAlert('환불 실패', getMutationErrorMessage(error, '환불 처리에 실패했습니다.'), true);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteMutation.mutateAsync({
                centerId,
                subscriptionId: subscription._id,
                memberId,
            });
            setShowDeleteConfirm(false);
            onClose();
            createAlert('삭제 완료', '이용권이 삭제되었습니다.', false);
        } catch (error) {
            createAlert('삭제 실패', getMutationErrorMessage(error, '이용권 삭제에 실패했습니다.'), true);
        }
    };

    const handleSubmit = async (force = false) => {
        if (!isValid) return;
        const body: any = {
            ...(startDate !== initialDates.startDate ? { startDate } : {}),
            ...(endDate !== initialDates.endDate ? { endDate } : {}),
            price: Number(price),
            paymentDate,
            paymentMethod,
            memo: memo.trim() || undefined,
        };
        // 잠긴 칸은 아예 보내지 않는다. 같은 값이면 서버가 무시하지만, 원래 결제일이 비어 있던 문서는
        // 이 창이 오늘 날짜를 채워 「다른 값」이 되어 409 가 나기 때문이다.
        if (transferLocks.paymentLocked) {
            delete body.price;
            delete body.paymentDate;
            delete body.paymentMethod;
        }
        if (transferLocks.periodLocked) {
            delete body.startDate;
            delete body.endDate;
        }
        if (subscription?.category === 'locker' && !transferLocks.periodLocked) {
            const nextLockerNumber = lockerNumber.trim();
            const nextLockNumber = lockNumber.trim();
            if (nextLockerNumber && nextLockerNumber !== (subscription.lockerNumber || '')) {
                body.lockerNumber = nextLockerNumber;
            }
            if (nextLockNumber !== (subscription.lockNumber || '')) {
                /*
                 * 비우면 `null` 을 보낸다.
                 *
                 * `undefined` 는 JSON 에서 키째 사라지고, 서버는 미전송을 **"변경 없음"** 으로
                 * 읽는다(facilitySubscription.service.ts 의 `!== undefined` 판정).
                 * 그래서 지금까지는 비밀번호를 지울 방법이 없었다 — 사용자는 "수정 완료"를
                 * 보지만 값은 그대로 남아 있었다.
                 */
                body.lockNumber = nextLockNumber === '' ? null : nextLockNumber;
            }
        }
        if (force) body.force = true;
        try {
            await updateMutation.mutateAsync({
                centerId,
                memberId,
                subscriptionId: subscription._id,
                body,
            });
            onClose();
            createAlert('수정 완료', '이용권이 수정되었습니다.', false);
        } catch (error) {
            // 라커 중복 경고면 확인 후 강행 수정
            if (handleLockerConflict(error, createAlert, () => handleSubmit(true), { confirmText: '수정하기' })) return;
            createAlert('수정 실패', describeSubscriptionUpdateError(error, '이용권 수정에 실패했습니다.'), true);
        }
    };

    if (!isVisible) return null;

    const inputClass =
        'border border-[#E1E4E6] rounded-[8px] px-[12px] py-[10px] text-[14px] w-full focus:outline-none focus:border-blue-400 transition-colors disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF] disabled:cursor-not-allowed';

    // 푸터 버튼 통일 스타일 (높이/패딩/폰트/borderRadius 동일)
    const btnBase: React.CSSProperties = {
        height: 38,
        padding: '0 18px',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        border: '1px solid transparent',
        transition: 'all 0.15s',
    };
    const btnStyles = {
        primary: { ...btnBase, backgroundColor: '#3B82F6', color: 'white' },
        neutral: {
            ...btnBase,
            backgroundColor: 'white',
            color: '#374151',
            border: '1px solid #D1D5DB',
        },
        danger: {
            ...btnBase,
            backgroundColor: 'white',
            color: '#EF4444',
            border: '1px solid #FCA5A5',
        },
        warning: {
            ...btnBase,
            backgroundColor: 'white',
            color: '#D97706',
            border: '1px solid #FCD34D',
        },
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
            onClick={onClose}
        >
            <Col
                style={{
                    width: 480,
                    maxHeight: '80vh',
                    backgroundColor: '#fff',
                    borderRadius: 16,
                    padding: 32,
                    overflowY: 'auto',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
                <Row style={{ alignItems: 'center', gap: 8 }}>
                    <Subtitle1Bold>이용권 수정</Subtitle1Bold>
                    <TransferKindBadge kind={transferLocks.kind} />
                </Row>
                {transferLocks.lockReason && (
                    <div className="mt-[12px] rounded-[10px] border border-solid border-[#E2E8F0] bg-[#F8FAFC] px-[14px] py-[10px] text-[13px] leading-[1.6] text-[#475569]" style={{ whiteSpace: 'pre-line' }}>
                        {transferLocks.lockReason}
                    </div>
                )}

                {/* Product info (read-only) */}
                <Col
                    style={{
                        marginTop: 20,
                        padding: 16,
                        borderRadius: 10,
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E2E8F0',
                        gap: 4,
                    }}
                >
                    <Row style={{ alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>{subscription.productName}</span>
                        <span
                            style={{
                                fontSize: 11,
                                padding: '2px 8px',
                                borderRadius: 8,
                                backgroundColor: '#F0F9FF',
                                color: '#0369A1',
                                fontWeight: 600,
                            }}
                        >
                            {subscription.category}
                        </span>
                    </Row>
                </Col>

                <Col style={{ marginTop: 20, gap: 16 }}>
                    {/* Start Date */}
                    <Col style={{ gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>시작일</Body2Regular>
                        <input className={inputClass} type="date" value={startDate} disabled={transferLocks.periodLocked} onChange={(e) => setStartDate(e.target.value)} />
                    </Col>

                    {/* End Date */}
                    <Col style={{ gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>종료일</Body2Regular>
                        <input
                            className={inputClass}
                            type="date"
                            value={endDate}
                            disabled={transferLocks.periodLocked}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ borderColor: dateError ? '#EF4444' : undefined }}
                        />
                        {dateError && <span style={{ fontSize: 12, color: '#EF4444' }}>{dateError}</span>}
                    </Col>

                    {/* Price */}
                    <Col style={{ gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>금액</Body2Regular>
                        <input
                            className={inputClass}
                            type="text"
                            inputMode="numeric"
                            placeholder="금액"
                            disabled={transferLocks.paymentLocked}
                            value={formatAmountText(price)}
                            onChange={(e) => {
                                // 원칙 1 — 패턴에 안 맞으면 값을 아예 바꾸지 않는다.
                                const digits = amountCaret.changeAmount(e, price);
                                if (digits == null) return;
                                setPrice(digits);
                            }}
                            // 붙여넣기는 한 번에 값이 확정되므로 정규형만 받는다(CSV 조각 차단).
                            onPaste={amountCaret.pasteAmount}
                            onDrop={amountCaret.dropAmount}
                        />
                    </Col>

                    {/* Payment Method */}
                    <Col style={{ gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>결제 수단</Body2Regular>
                        <Row style={{ gap: 8 }}>
                            {PAYMENT_OPTIONS.map((opt) => (
                                <button
                                    key={opt.code}
                                    disabled={transferLocks.paymentLocked}
                                    style={{
                                        flex: 1,
                                        padding: '8px 0',
                                        borderRadius: 8,
                                        border: `1px solid ${paymentMethod === opt.code ? '#3B82F6' : '#E1E4E6'}`,
                                        backgroundColor: paymentMethod === opt.code ? '#EFF6FF' : '#fff',
                                        color: paymentMethod === opt.code ? '#3B82F6' : '#64748B',
                                        fontSize: 13,
                                        fontWeight: paymentMethod === opt.code ? 600 : 400,
                                        cursor: transferLocks.paymentLocked ? 'not-allowed' : 'pointer',
                                        opacity: transferLocks.paymentLocked && paymentMethod !== opt.code ? 0.5 : 1,
                                        transition: 'all 0.2s',
                                    }}
                                    onClick={() => setPaymentMethod(opt.code)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </Row>
                    </Col>

                    {/* Payment Date */}
                    <Col style={{ gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>결제일</Body2Regular>
                        <input className={inputClass} type="date" value={paymentDate} disabled={transferLocks.paymentLocked} onChange={(e) => setPaymentDate(e.target.value)} />
                    </Col>

                    {/* Memo */}
                    <Col style={{ gap: 6 }}>
                        <Body2Regular color={colors.LAVEL_2}>메모</Body2Regular>
                        <textarea
                            className={`${inputClass} resize-none`}
                            rows={3}
                            placeholder="메모 (선택)"
                            value={memo}
                            onChange={(e) => setMemo(e.target.value)}
                        />
                    </Col>

                    {/* 라커 배정 필드 */}
                    {subscription?.category === 'locker' && !transferLocks.periodLocked && (
                        <Col className="gap-[12px] p-[16px] bg-[#F8FAFC] rounded-[10px] border border-solid border-[#E2E8F0]">
                            <Row className="items-center gap-[6px] mb-[4px]">
                                <Subtitle1Bold color={colors.LAVEL_1} style={{ fontSize: 14 }}>
                                    라커 배정
                                </Subtitle1Bold>
                            </Row>
                            <Col className="gap-[6px]">
                                <Row className="items-center gap-[4px]">
                                    <label className="text-[12px] font-bold text-gray-700">번호</label>
                                    <span className="text-[11px] text-red-500">*</span>
                                </Row>
                                <input
                                    className={inputClass}
                                    placeholder="예: 12"
                                    value={lockerNumber}
                                    onChange={(e) => setLockerNumber(e.target.value)}
                                    style={{
                                        borderColor: lockerRequired ? '#EF4444' : undefined,
                                    }}
                                />
                            </Col>
                            <Col className="gap-[6px]">
                                <label className="text-[12px] font-bold text-gray-700">자물쇠 비밀번호 (선택)</label>
                                <input className={inputClass} placeholder="예: 1234" value={lockNumber} onChange={(e) => setLockNumber(e.target.value)} />
                                {/* 회원 개별값이다. 칸에 고정된 비밀번호와 다른 값이다. */}
                                <span className="text-[11px] text-gray-500">이 값은 이 회원에게만 적용됩니다. 자리에 고정된 비밀번호는 「락커 관리」에서 설정하세요.</span>
                            </Col>
                            {lockerRequired && <span className="text-[11px] text-red-500 mt-[4px]">라커 번호는 필수입니다.</span>}
                        </Col>
                    )}

                    {/* 일시정지 관리 (정지/재개/취소/예약 + 이력). 전부 양도한 이용권은 서버가 막는다. */}
                    {transferLocks.canPause && (
                        <SubscriptionPauseManager
                            centerId={centerId}
                            memberId={memberId}
                            subscription={subscription}
                            createAlert={createAlert}
                            onChanged={onClose}
                        />
                    )}
                </Col>

                <Row
                    style={{
                        marginTop: 24,
                        gap: 8,
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Row style={{ gap: 8 }}>
                        {transferLocks.canDelete && (
                            <button onClick={() => setShowDeleteConfirm(true)} style={btnStyles.danger}>
                                이용권 삭제
                            </button>
                        )}
                        {subscription?.status === 'active' && transferLocks.canRefund && !isViewerAccount && (
                            <button onClick={openRefund} style={btnStyles.warning}>
                                환불
                            </button>
                        )}
                        {(subscription?.status === 'active' || hasPendingTransfer) && onTransfer && !isViewerAccount && (
                            <button onClick={onTransfer} style={{ ...btnStyles.neutral, color: '#2563EB', border: '1px solid #BFDBFE' }}>
                                {hasPendingTransfer ? '이어서 양도하기' : '양도'}
                            </button>
                        )}
                    </Row>
                    <Row style={{ gap: 8 }}>
                        <button onClick={onClose} style={btnStyles.neutral}>
                            취소
                        </button>
                        <button
                            onClick={() => handleSubmit()}
                            disabled={!isValid || updateMutation.isLoading}
                            style={{
                                ...btnStyles.primary,
                                opacity: !isValid || updateMutation.isLoading ? 0.5 : 1,
                                cursor: !isValid || updateMutation.isLoading ? 'not-allowed' : 'pointer',
                            }}
                        >
                            수정
                        </button>
                    </Row>
                </Row>

                {showDeleteConfirm && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1100,
                        }}
                        onClick={() => setShowDeleteConfirm(false)}
                    >
                        <Col
                            style={{
                                width: 380,
                                backgroundColor: '#fff',
                                borderRadius: 14,
                                padding: 24,
                                gap: 8,
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                            <Subtitle1Bold>이용권 삭제</Subtitle1Bold>
                            <span
                                style={{
                                    fontSize: 13,
                                    color: '#475569',
                                    lineHeight: 1.6,
                                    marginTop: 4,
                                }}
                            >
                                '{subscription.productName}' 이용권을 삭제하시겠습니까?
                                <br />
                                삭제된 이용권은 복구할 수 없습니다.
                            </span>
                            <Row style={{ marginTop: 16, gap: 8, justifyContent: 'flex-end' }}>
                                <button onClick={() => setShowDeleteConfirm(false)} style={btnStyles.neutral}>
                                    취소
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleteMutation.isLoading}
                                    style={{
                                        ...btnStyles.primary,
                                        backgroundColor: '#EF4444',
                                        opacity: deleteMutation.isLoading ? 0.6 : 1,
                                        cursor: deleteMutation.isLoading ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    삭제
                                </button>
                            </Row>
                        </Col>
                    </div>
                )}

                {showRefundConfirm && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1100,
                        }}
                        onClick={() => setShowRefundConfirm(false)}
                    >
                        <Col
                            style={{
                                width: 420,
                                backgroundColor: '#fff',
                                borderRadius: 14,
                                padding: 24,
                                gap: 16,
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                            <Subtitle1Bold>이용권 환불</Subtitle1Bold>
                            <Col
                                style={{
                                    gap: 6,
                                    padding: '12px 14px',
                                    backgroundColor: '#F8FAFC',
                                    borderRadius: 8,
                                }}
                            >
                                <span style={{ fontSize: 13, color: '#475569' }}>{subscription.productName}</span>
                                <span style={{ fontSize: 12, color: '#64748B' }}>
                                    결제 금액: <b style={{ color: '#1F2937' }}>{(subscription.price ?? 0).toLocaleString()}원</b>
                                </span>
                            </Col>

                            {transferLocks.refundNeedsAck && <TransferredRefundNotice info={transferredInfo} />}

                            <Col style={{ gap: 6 }}>
                                <Body2Regular color={colors.LAVEL_2}>
                                    {transferLocks.refundNeedsAck ? '돌려줄 금액' : '환불 금액 (전액/부분 모두 가능)'}
                                </Body2Regular>
                                <input
                                    className={inputClass}
                                    type="text"
                                    inputMode="numeric"
                                    value={formatAmountText(refundAmount)}
                                    onChange={(e) => {
                                        // 원칙 1 — 패턴에 안 맞으면 값을 아예 바꾸지 않는다.
                                        const digits = amountCaret.changeAmount(e, refundAmount);
                                        if (digits == null) return;
                                        setRefundAmount(digits);
                                    }}
                                    // 붙여넣기는 한 번에 값이 확정되므로 정규형만 받는다(CSV 조각 차단).
                                    onPaste={amountCaret.pasteAmount}
                                    onDrop={amountCaret.dropAmount}
                                    placeholder={transferLocks.refundNeedsAck ? '돌려줄 금액을 적어 주세요' : '환불할 금액'}
                                />
                                {!transferLocks.refundNeedsAck && <Row style={{ gap: 6, marginTop: 4 }}>
                                    <button
                                        type="button"
                                        onClick={() => { amountCaret.dropPendingCaret(); setRefundAmount(String(subscription.price ?? 0)); }}
                                        style={{
                                            padding: '4px 10px',
                                            fontSize: 11,
                                            borderRadius: 6,
                                            border: '1px solid #E5E7EB',
                                            backgroundColor: '#F9FAFB',
                                            color: '#374151',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        전액
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { amountCaret.dropPendingCaret(); setRefundAmount(String(Math.floor((subscription.price ?? 0) / 2))); }}
                                        style={{
                                            padding: '4px 10px',
                                            fontSize: 11,
                                            borderRadius: 6,
                                            border: '1px solid #E5E7EB',
                                            backgroundColor: '#F9FAFB',
                                            color: '#374151',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        반액
                                    </button>
                                </Row>}
                            </Col>

                            <Col style={{ gap: 6 }}>
                                <Body2Regular color={colors.LAVEL_2}>환불 사유 (선택)</Body2Regular>
                                <input
                                    className={inputClass}
                                    value={refundReason}
                                    onChange={(e) => setRefundReason(e.target.value)}
                                    placeholder="예: 회원 요청"
                                />
                            </Col>

                            <Row style={{ gap: 8, justifyContent: 'flex-end' }}>
                                <button onClick={() => setShowRefundConfirm(false)} style={btnStyles.neutral}>
                                    취소
                                </button>
                                <button
                                    onClick={handleRefund}
                                    disabled={refundMutation.isLoading || refundBlockedByTransferInfo}
                                    style={{
                                        ...btnStyles.primary,
                                        backgroundColor: '#D97706',
                                        opacity: refundMutation.isLoading || refundBlockedByTransferInfo ? 0.6 : 1,
                                        cursor: refundMutation.isLoading || refundBlockedByTransferInfo ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    환불 처리
                                </button>
                            </Row>
                        </Col>
                    </div>
                )}
            </Col>
        </div>
    );
};

export default SubscriptionEditModal;
